import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useTheme } from "@mui/material/styles";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ShowActiveLinkLogs() {
  const navigate = useNavigate();
  const { linkId } = useParams();
  const { linkName, linkType, linkCreation, linkExpiry } = useLocation().state;

  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Fetch data function
  const fetchLinkData = useCallback(async () => {
    try {
      setLoading(true);
      const linkURL = `${API_BASE_URL}/api/auth/activity/link/${linkId}`;
      const linkRes = await axios.get(linkURL);
      setLinkData(linkRes.data?.data);
    } catch (err) {
      console.error("Error fetching link activity:", err);
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  // Initial load
  useEffect(() => {
    fetchLinkData();
  }, [fetchLinkData]);

  // Date & Time formatter
  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Readable date
  function formatDateToReadable(dateString) {
    const date = new Date(dateString);
    const day = date.getUTCDate();
    const month = date.toLocaleString("en-US", {
      month: "short",
      timeZone: "UTC",
    });
    const year = date.getUTCFullYear();

    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return "th";
      switch (n % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    return `${day}${getOrdinal(day)} ${month} ${year}`;
  }

  return (
    <Box
      sx={{
        bgcolor: "#394A5C",
        minHeight: "100vh",
        p: isMobile ? 2 : 6,
      }}
    >
      {/* Top Actions */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          mb: 2,
        }}
      >
        {/* Back Button */}
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: "#FFF",
            "&:hover": { bgcolor: "#e0e0e0" },
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fill: "#42A996" }} />
        </IconButton>

        {/* Home Button */}
        <IconButton
          onClick={() => navigate("/admin-dash")}
          sx={{
            bgcolor: "#FFF",
            "&:hover": { bgcolor: "#e0e0e0" },
          }}
        >
          <HomeRoundedIcon sx={{ fill: "#42A996" }} />
        </IconButton>

        {/* Refresh Button */}
        <Tooltip title="Refresh activity">
          <IconButton
            onClick={fetchLinkData}
            sx={{
              bgcolor: "#FFF",
              ml: "auto",
              "&:hover": { bgcolor: "#e0e0e0" },
            }}
          >
            <RefreshRoundedIcon
              sx={{
                fill: loading ? "#888" : "#42A996",
                animation: loading ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Link Details Box */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          mb: 3,
          borderRadius: 2,
          px: 2,
          py: 2,
          width: isMobile ? "100%" : "50%",
          bgcolor:
            linkType === "public"
              ? "rgba(29, 56, 29, 0.95)"
              : "rgba(56, 29, 30, 0.95)",
          position: "relative",
        }}
      >
        <Typography sx={{ color: "#fff", fontSize: isMobile ? 16 : 18 }}>
          Link Name:{" "}
          <span style={{ fontWeight: 600, fontSize: isMobile ? 18 : 20 }}>
            {linkName}
          </span>
        </Typography>

        {/* Type badge */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 12,
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          {linkType === "public" ? (
            <LanguageRoundedIcon sx={{ fontSize: 16, fill: "#FFF" }} />
          ) : (
            <SecurityRoundedIcon sx={{ fontSize: 16, fill: "#FFF" }} />
          )}
          <Typography sx={{ color: "#FFF", fontWeight: 600 }}>
            {linkType === "public" ? "Public" : "Private"}
          </Typography>
        </Box>

        <Typography sx={{ color: "#FFF" }}>
          Created On: <b>{formatDateToReadable(linkCreation)}</b>
        </Typography>

        <Typography sx={{ color: "#FFF" }}>
          Expires On: <b>{formatDateToReadable(linkExpiry)}</b>
        </Typography>
      </Box>

      {/* Activity Table */}
      <Box
        sx={{
          bgcolor: "#2A3A4B",
          borderRadius: 2,
          overflowX: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Product Tag No.
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Action Taken
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Action Taken By
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Action Taken At
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(!linkData || linkData.length === 0) && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    bgcolor: "#1E2F40",
                    color: "#FFF",
                    fontSize: 18,
                    py: 3,
                  }}
                >
                  Nothing happened on this link
                </TableCell>
              </TableRow>
            )}

            {linkData?.map((link, index) => (
              <TableRow
                key={index}
                sx={{
                  bgcolor: index % 2 === 0 ? "#1E2F40" : "#2A3A4B",
                }}
              >
                <TableCell sx={{ color: "#fff" }}>
                  {link.tag_no || "--"}
                </TableCell>

                <TableCell
                  sx={{ color: "#42A996", textTransform: "capitalize" }}
                >
                  {link.action_type === "LOGIN" && "Logged in"}
                  {link.action_type === "REQUEST_ACCESS" && "Requested access"}
                  {(link.action_type === "LIKED_PRODUCT" ||
                    link.action_type === "VIEW_STOCK") &&
                    "Liked the product"}
                  {link.action_type === "ADD_CART" && "Added to cart"}
                  {link.action_type === "REMOVE_CART" && "Removed from cart"}
                </TableCell>

                <TableCell sx={{ color: "#fff" }}>{link.user_name}</TableCell>

                <TableCell sx={{ color: "#fff" }}>
                  {formatDate(link.created_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default ShowActiveLinkLogs;
